import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiEye,
  FiHome,
  FiMapPin,
  FiPlus,
  FiSearch,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";
import { MdOutlineWarehouse } from "react-icons/md";
import { RoutesPaths } from "../Router/constant";
import {
  assertApiSuccess,
  deleteWarehouse,
  extractWarehouseList,
  getApiMessage,
  getWarehouses,
  setWarehouseStatus,
} from "../Services/warehouseService";
import ConfirmationModal from "../Components/ConfirmationModal";
import Skeleton from "../Components/SkeletonLoader";
import AdminOnly from "../Components/AdminOnly";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

export const formatDimensions = (dimensions) => {
  const { length, width, height } = dimensions || {};
  if ([length, width, height].every((v) => v === undefined || v === null || v === "")) {
    return "--";
  }
  return `${length ?? "-"} × ${width ?? "-"} × ${height ?? "-"} m`;
};

export const warehouseImageUrl = (fileName) =>
  fileName ? `${process.env.REACT_APP_IMAGE_URL}/${fileName}` : "";

function WarehouseList() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(1);
  const [limit] = useState(10);
  const [count, setCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [isActive, setIsActive] = useState("");
  // type: "delete" | "status"
  const [confirmation, setConfirmation] = useState({ show: false, type: "", warehouse: null });
  const [working, setWorking] = useState(false);
  const closeConfirmation = () => !working && setConfirmation({ show: false, type: "", warehouse: null });

  // Debounce the search box so we don't call the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchTerm.trim());
      setOffset(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchWarehouses = async () => {
    setLoading(true);
    try {
      const response = await getWarehouses({ offset, limit, search, isActive });
      const { list, count: total } = extractWarehouseList(response);
      setWarehouses(list);
      setCount(total);
    } catch (error) {
      console.error("Get warehouses error:", error);
      toast.error(getApiMessage(error, "Failed to load warehouses"));
      setWarehouses([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, search, isActive]);

  const handleConfirm = async () => {
    const { type, warehouse } = confirmation;
    const id = warehouse?._id;
    if (!id || working) return;

    setWorking(true);
    try {
      if (type === "delete") {
        const response = assertApiSuccess(await deleteWarehouse(id), "Failed to delete warehouse");
        toast.success(getApiMessage(response, "Warehouse deleted successfully"));
        // step back a page if we just removed the last row on it
        if (warehouses.length === 1 && offset > 1) setOffset(offset - 1);
        else fetchWarehouses();
      } else {
        const nextActive = warehouse.isActive === false;
        const response = assertApiSuccess(
          await setWarehouseStatus(id, nextActive),
          "Failed to update warehouse status",
        );
        toast.success(
          getApiMessage(response, nextActive ? "Warehouse activated" : "Warehouse deactivated"),
        );
        fetchWarehouses();
      }
      setConfirmation({ show: false, type: "", warehouse: null });
    } catch (error) {
      console.error("Warehouse action error:", error);
      // e.g. "Warehouse cannot be deleted because an order already uses this warehouse."
      toast.error(
        getApiMessage(error, type === "delete" ? "Failed to delete warehouse" : "Failed to update warehouse status"),
      );
    } finally {
      setWorking(false);
    }
  };

  const totalPages = Math.ceil(count / limit);
  const firstRow = count === 0 ? 0 : (offset - 1) * limit + 1;
  const lastRow = Math.min(offset * limit, count);

  return (
    <div className="container-fluid qd-page">
      <nav className="qd-breadcrumb">
        <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
          <FiHome />
        </NavLink>
        <FiChevronRight />
        <span className="qd-breadcrumb-current">Warehouse</span>
      </nav>

      {/* Header */}
      <div className="qd-card qa-header">
        <div className="qd-panel-title">
          <span className="qa-header-icon">
            <MdOutlineWarehouse />
          </span>
          <div>
            <h1 className="qa-title">Warehouses</h1>
            <p className="qd-panel-sub qa-sub">Manage storage locations, their size and photos.</p>
          </div>
        </div>
        <div className="qd-bookings-tools">
          <select
            className="qd-select qd-select-wide"
            value={isActive}
            onChange={(e) => {
              setIsActive(e.target.value);
              setOffset(1);
            }}
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label className="qd-search qc-search">
            <FiSearch />
            <input
              type="text"
              placeholder="Search name or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
          <NavLink to={RoutesPaths.ADDWAREHOUSE} className="qd-btn qd-btn-primary qa-create">
            <FiPlus /> Create warehouse
          </NavLink>
        </div>
      </div>

      {/* Table */}
      <div className="qd-card qd-panel">
        <div className="qd-table-wrap">
          <table className="qd-table qw-table">
            <thead>
              <tr>
                <th>Warehouse</th>
                <th>Location</th>
                <th>Dimensions (L × W × H)</th>
                <th>Status</th>
                <th>Created</th>
                <th className="qb-th-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td><Skeleton width={200} height={40} /></td>
                    <td><Skeleton width={180} /></td>
                    <td><Skeleton width={110} /></td>
                    <td><Skeleton width={80} height={24} /></td>
                    <td><Skeleton width={90} /></td>
                    <td><Skeleton width={150} height={34} /></td>
                  </tr>
                ))
              ) : warehouses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="qu-empty-cell">
                    <div className="qu-empty">
                      <span className="qu-empty-art">
                        <MdOutlineWarehouse />
                      </span>
                      <h6>{search || isActive ? "No warehouses match your filters" : "No warehouses yet"}</h6>
                      <p>
                        {search || isActive
                          ? "Try a different search or status."
                          : "Create your first warehouse to get started."}
                      </p>
                      {!search && !isActive && (
                        <NavLink to={RoutesPaths.ADDWAREHOUSE} className="qd-btn qd-btn-primary">
                          <FiPlus /> Create warehouse
                        </NavLink>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                warehouses.map((warehouse) => {
                  const active = warehouse?.isActive !== false;
                  const cover = warehouse?.uploadImages?.[0];
                  return (
                    <tr key={warehouse._id}>
                      <td>
                        <span className="qw-name-cell">
                          <span className="qw-thumb">
                            {cover ? (
                              <img src={warehouseImageUrl(cover)} alt="" />
                            ) : (
                              <MdOutlineWarehouse />
                            )}
                          </span>
                          <span className="qw-name">
                            <strong>{warehouse.name || "Untitled warehouse"}</strong>
                            {warehouse.description && <small>{warehouse.description}</small>}
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className="qw-location-cell">
                          <FiMapPin />
                          {warehouse.location || "--"}
                        </span>
                      </td>
                      <td className="qd-mono">{formatDimensions(warehouse.dimensions)}</td>
                      <td>
                        <span className={`qc-status ${active ? "qp-status-accepted" : "qp-status-rejected"}`}>
                          <span className="qu-dot" />
                          {active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <span className="qd-date">
                          <FiCalendar />
                          {warehouse.createdAt
                            ? new Date(warehouse.createdAt).toLocaleDateString("en-GB")
                            : "--"}
                        </span>
                      </td>
                      <td>
                        <div className="qd-actions qb-actions">
                          <NavLink
                            to={`${RoutesPaths.WAREHOUSEDETAIL}/${warehouse._id}`}
                            className="qb-icon-action"
                            title="View"
                            aria-label={`View ${warehouse.name}`}
                          >
                            <FiEye />
                          </NavLink>
                          <button
                            type="button"
                            className="qb-icon-action"
                            title="Edit"
                            aria-label={`Edit ${warehouse.name}`}
                            onClick={() => navigate(`${RoutesPaths.EDITWAREHOUSE}/${warehouse._id}`)}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            type="button"
                            className={`qb-icon-action qw-toggle ${active ? "is-on" : "is-off"}`}
                            title={active ? "Deactivate" : "Activate"}
                            aria-label={`${active ? "Deactivate" : "Activate"} ${warehouse.name}`}
                            onClick={() => setConfirmation({ show: true, type: "status", warehouse })}
                          >
                            {active ? <FiToggleRight /> : <FiToggleLeft />}
                          </button>
                          <button
                            type="button"
                            className="qb-icon-action qw-delete"
                            title="Delete"
                            aria-label={`Delete ${warehouse.name}`}
                            onClick={() => setConfirmation({ show: true, type: "delete", warehouse })}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && count > 0 && (
          <div className="qd-pagination">
            <span className="qd-muted">
              Showing {firstRow} to {lastRow} of {count} warehouses
            </span>
            {totalPages > 1 && (
              <div className="qd-pages">
                <button disabled={offset <= 1} onClick={() => setOffset(offset - 1)} aria-label="Previous">
                  <FiChevronLeft />
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index}
                    className={offset === index + 1 ? "active" : ""}
                    onClick={() => setOffset(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  disabled={offset >= totalPages}
                  onClick={() => setOffset(offset + 1)}
                  aria-label="Next"
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <WarehouseActionModal
        confirmation={confirmation}
        working={working}
        onConfirm={handleConfirm}
        onCancel={closeConfirmation}
      />
    </div>
  );
}

/* Shared by the list and detail pages: confirm delete / activate / deactivate. */
export function WarehouseActionModal({ confirmation, working, onConfirm, onCancel }) {
  const { show, type, warehouse } = confirmation;
  const name = <strong>{warehouse?.name || "This warehouse"}</strong>;

  if (type === "delete") {
    return (
      <ConfirmationModal
        show={show}
        title="Delete warehouse"
        variant="danger"
        message={
          <p>
            {name} will be <strong>permanently deleted</strong>. This can&apos;t be undone. A warehouse
            that is already used by an order can&apos;t be deleted &mdash; deactivate it instead.
          </p>
        }
        onConfirm={onConfirm}
        onCancel={onCancel}
        confirmText={working ? "Deleting..." : "Yes, delete"}
        confirmDisabled={working}
        cancelText="Cancel"
      />
    );
  }

  const activating = warehouse?.isActive === false;
  return (
    <ConfirmationModal
      show={show}
      title={activating ? "Activate warehouse" : "Deactivate warehouse"}
      variant={activating ? "success" : "default"}
      icon={activating ? FiToggleRight : FiToggleLeft}
      message={
        <p>
          {activating ? (
            <>
              {name} will be <strong>active</strong> again and can be selected on new orders.
            </>
          ) : (
            <>
              {name} will be <strong>inactive</strong> and won&apos;t appear when creating new orders.
              Existing orders aren&apos;t affected, and you can activate it again at any time.
            </>
          )}
        </p>
      }
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmText={working ? "Saving..." : activating ? "Yes, activate" : "Yes, deactivate"}
      confirmDisabled={working}
      cancelText="Cancel"
    />
  );
}

export default function Warehouse() {
  return (
    <AdminOnly>
      <WarehouseList />
    </AdminOnly>
  );
}
